import type { ReactNode } from 'react';

import PageShell from './PageShell';
import ConnectBar, { type ConnectionStatus } from './ConnectBar';
import SelectionBar from './SelectionBar';

type SourceShellProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Действия в правой части шапки (CTA, sync). */
  actions?: ReactNode;
  /** Опциональный фильтр/поиск во второй строке шапки. */
  toolbar?: ReactNode;

  /** Подключение к источнику. Если не передано — ConnectBar не рендерится. */
  connection?: {
    status: ConnectionStatus;
    label: string;
    hint?: ReactNode;
    onConnect?: () => void;
    onDisconnect?: () => void;
    connectLabel?: string;
  };

  /** Полоса bulk-действий внизу. Появляется когда count > 0. */
  selection?: {
    count: number;
    hint?: ReactNode;
    actions: ReactNode;
  };

  /** Контент модуля — обычно SplitPane или таблица/сетка. */
  children: ReactNode;
};

/**
 * Единый каркас для модулей-источников (Git, Confluence, Jira и т.п.).
 * Добавляет к PageShell:
 *   - ConnectBar сверху (если переданы данные подключения)
 *   - SelectionBar снизу (если есть выбранные элементы)
 *
 * Контент-зона остаётся под управлением модуля и не скроллит — внутри либо
 * SplitPane, либо собственный layout с прокруткой.
 */
const SourceShell = ({
  title,
  subtitle,
  actions,
  toolbar,
  connection,
  selection,
  children,
}: SourceShellProps) => {
  return (
    <PageShell
      title={title}
      subtitle={subtitle}
      actions={actions}
      toolbar={toolbar}
      noContentPadding
      bodyScrolls={false}
    >
      {connection && (
        <ConnectBar
          status={connection.status}
          label={connection.label}
          hint={connection.hint}
          onConnect={connection.onConnect}
          onDisconnect={connection.onDisconnect}
          connectLabel={connection.connectLabel}
        />
      )}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
      {selection && (
        <SelectionBar count={selection.count} hint={selection.hint}>
          {selection.actions}
        </SelectionBar>
      )}
    </PageShell>
  );
};

export default SourceShell;
